import React, {useMemo, useState} from 'react'
import FilterButton from '@feature/leaderboard/components/FilterButton/FilterButton'
import Text from '@component/Text/Text'
import IconUp from '@svg/common/ic_btn_up.svg'
import IconDown from '@svg/common/ic_btn_down.svg'
import {useTranslation} from '@lib/intl/useTranslations'
import useLogin from '@hook/useLogin'
import useIsMount from '@hook/useIsMount'
import WizSelectBox, {WSOption} from '@component/select/WizSelectBox'
import useQueryGetExchanges from '@hook/query/core/exchange/useQueryGetExchanges'
import {createLog} from '@util/logs'
import {ExchangeType} from '@type/exchange'
import WizSegmentedControl from '@component/segmentedControl/WizSegmentedControl'
import {useMainLeaderBoardFilter} from '@hook/useLeaderBoardFilter'
import SearchBar from '@feature/leaderboard/components/SearchBar/SearchBar'
import {useLoginModalStore} from '@store/LoginModalStore'

export type RankKind = 'following' | 'normal'
export type RankPeriod = '1' | '30' | '1000'
export type RankSortBy = 'balance' | 'profit' | 'earning' | 'aprofit' | ''

interface IProps {}

const MainLeaderboardOptionContainer: React.FC<IProps> = ({}) => {
    const t = useTranslation()

    const {isLogin} = useLogin()
    const {isMount} = useIsMount()

    const {data: exchanges} = useQueryGetExchanges()

    const {mainFilter, setMainFilter} = useMainLeaderBoardFilter()
    const {exchange: filterExchange, period: filterPeriod, rankKind, sortBy} = mainFilter

    const showLoginModal = useLoginModalStore(state => state.showLoginModal)

    const [showFilter, setShowFilter] = useState(false)

    const toggleFollowing = () => {
        if (!isLogin()) {
            showLoginModal()
            return
        }
        if (rankKind === 'following') {
            setMainFilter({...mainFilter, rankKind: 'normal'})
        } else {
            setMainFilter({...mainFilter, exchange: 'all', rankKind: 'following'})
        }
        createLog('event', 'leaderboard_main_sort_following', {rankKind: rankKind})
    }

    const exchangeOptions = useMemo(() => {
        const list: WSOption[] = [
            {
                id: 'all',
                text: t('leaderboard.filter.button.allExchanges'),
                icon: '/svgs/common/ic_exchanges_all.svg',
                value: '',
            },
        ]
        exchanges?.map(item =>
            list.push({id: item?.name, text: item?.display_name, icon: item?.icon, value: item?.name}),
        )
        return list
    }, [exchanges, t])

    const sortOptions = useMemo(() => {
        const list: WSOption[] = []
        list.push({id: 'profit', text: t('leaderboard.filter.button.profit'), value: 'profit'})
        list.push({id: 'balance', text: t('leaderboard.filter.button.balance'), value: 'balance'})
        list.push({id: 'earning', text: t('leaderboard.filter.button.earning'), value: 'earning'})
        list.push({id: 'aprofit', text: t('leaderboard.filter.button.aprofit'), value: 'aprofit'})
        return list
    }, [t])

    const periodOptions = useMemo(() => {
        const list: WSOption[] = []
        if (sortBy === '' || sortBy === 'profit') {
            list.push({id: '1', text: t('leaderboard.filter.button.24h')})
        }
        list.push({id: '30', text: t('leaderboard.filter.button.30D')})
        list.push({id: '1000', text: t('leaderboard.filter.button.allTime')})
        return list
    }, [t, sortBy])

    const onChangeExchange = (exchange: ExchangeType) => {
        createLog('event', 'leaderboard_main_sort_exchange', {exchange: exchange})
        setMainFilter({...mainFilter, exchange})
    }

    const onChangeSortType = (sort: RankSortBy) => {
        createLog('event', 'leaderboard_main_sort_type', {type: sort})
        setMainFilter({...mainFilter, sortBy: sort})
    }

    const onChangePeriod = (period: RankPeriod) => {
        createLog('event', 'leaderboard_main_sort_period', {period: period})
        setMainFilter({...mainFilter, period})
    }

    return (
        <>
            {/*Filter buttons on medium to big size*/}
            <div className={'flex justify-between items-center md:hidden'}>
                <div className={'flex items-center gap-x-[10px]'}>
                    {isMount && (
                        <FilterButton
                            showLeftTick={true}
                            dataCy={'btn_leaderboard_main_following'}
                            title={t('leaderboard.filter.button.following')}
                            isSelected={rankKind === 'following'}
                            onClick={toggleFollowing}
                        />
                    )}
                    <WizSelectBox
                        size={'small'}
                        className={'min-w-[180px]'}
                        selectedOption={filterExchange || exchangeOptions?.at(0)?.id}
                        options={exchangeOptions}
                        onChange={onChangeExchange}
                        disabled={
                            rankKind === 'following' ||
                            sortBy === 'balance' ||
                            sortBy === 'earning' ||
                            sortBy === 'aprofit'
                        }
                    />

                    <WizSelectBox
                        size={'small'}
                        className={'grow'}
                        selectedOption={sortBy || sortOptions?.at(0)?.id}
                        options={sortOptions}
                        onChange={onChangeSortType}
                    />
                </div>

                <div className={'flex items-center gap-x-[10px]'}>
                    {(sortBy === undefined || sortBy === 'aprofit' || sortBy === 'profit') && (
                        <WizSegmentedControl
                            selectedOption={filterPeriod || periodOptions?.at(-1)?.id}
                            options={periodOptions}
                            onChange={onChangePeriod}
                        />
                    )}
                    <SearchBar />
                </div>
            </div>

            <div className={'hidden md:grid grid-cols-2 gap-[7px]'}>
                <div className={'flex flex-col'}>
                    {isMount && (
                        <FilterButton
                            showLeftTick={true}
                            dataCy={'btn_leaderboard_main_following'}
                            title={t('leaderboard.filter.button.following')}
                            isSelected={rankKind === 'following'}
                            onClick={toggleFollowing}
                        />
                    )}
                </div>
                <div className={'flex items-center gap-x-[10px]'}>
                    <WizSelectBox
                        size={'small'}
                        className={'grow'}
                        optionClassName={''}
                        selectedOption={sortBy || sortOptions?.at(0)?.id}
                        options={sortOptions}
                        onChange={onChangeSortType}
                    />
                    <SearchBar />
                </div>
                <div className={'flex flex-col'}>
                    <WizSelectBox
                        size={'small'}
                        className={'grow'}
                        optionClassName={''}
                        selectedOption={filterExchange || exchangeOptions?.at(0)?.id}
                        options={exchangeOptions}
                        onChange={onChangeExchange}
                        disabled={
                            rankKind === 'following' ||
                            sortBy === 'balance' ||
                            sortBy === 'earning' ||
                            sortBy === 'aprofit'
                        }
                    />
                </div>
                <div className={'flex flex-col'}>
                    <WizSelectBox
                        size={'small'}
                        className={'grow'}
                        optionClassName={''}
                        selectedOption={filterPeriod || periodOptions?.at(-1)?.id}
                        options={periodOptions}
                        onChange={onChangePeriod}
                    />
                </div>
            </div>
        </>
    )
}

export default React.memo(MainLeaderboardOptionContainer)
